/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { ReferralForm } from './ReferralForm';
import { SubmitButton } from '../button/SubmitButton';
import React, { useMemo, useState } from 'react';
import { RootInitiativeData } from '../../types/initiative';
import { useAddReferralMutation, useGetReferralsQuery } from '../../api/referrals';
import { ProductCodes, SubscriptionService } from '../../services/SubscriptionService';
import { BasicAlert } from '../alert/BasicAlert';
import Loader from '../loader';
import { useAppDispatch } from '../../reducers';
import { reloadRootInitiatives } from '../../slice/rootInitiativeListSlice';
import { addSiteError } from '../../slice/siteAlertsSlice';

interface ReferralContainerFormParams {
  org: RootInitiativeData;
  productCode: ProductCodes
  backBtn?: JSX.Element;
  onSubmit?: () => void;
}

const MsgContainer = ({ children, className = '' }: { className?: string, children: React.ReactNode }) => {
  return (
    <div className={className}>
      <div className={'text-center mb-3'}><h2>Referral code</h2></div>
      {children}
    </div>
  )
}

export const ReferralContainerForm = (props: ReferralContainerFormParams) => {

  const { org, backBtn = null, productCode } = props;

  const dispatch = useAppDispatch();
  const [state, setState] = useState({ referenceCompany: '', referralCode: '' });

  const [handler, result] = useAddReferralMutation()

  const { isLoading, isSuccess, data: referrals } = useGetReferralsQuery({
    initiativeId: org._id,
    productCode,
  })


  const codes = useMemo(() => {
    return new Set(referrals?.map(r => r.code)?.filter(Boolean) ?? [])
  }, [referrals])

  if (isLoading) {
    return <Loader />
  }

  if (SubscriptionService.hasActive(productCode, org.calculatedSubscriptions)) {
    return <MsgContainer>
      <div className={'my-5 text-center text-ThemeSuccessLight'}>
        <div className={'mb-4'}><i className='fa-6x fas fa-check-circle'/></div>
        <h3>Active subscription is enabled</h3>
      </div>
    </MsgContainer>
  }

  if (!isSuccess) {
    return <MsgContainer>
      No referral codes can be applied at this moment
      {backBtn}
    </MsgContainer>
  }

  const hasDuplicate = state.referralCode && codes.has(state.referralCode)

  const updateForm = (key: string, value: string) => setState((s) => ({ ...s, [key]: value }));

  const onSubmit = async () => {
    return handler({
      initiativeId: org._id,
      productCode: productCode,
      referral: {
        code: state.referralCode,
        referrer: state.referenceCompany
      }
    }).then((response) => {
      if ('data' in response && response.data.success) {
        dispatch(reloadRootInitiatives())
        props.onSubmit?.()
      }
    }).catch(e => {
      // Could be partial failure, try to refresh anyway
      dispatch(reloadRootInitiatives())
      dispatch(addSiteError(e));
    })
  };

  return (
    <div>
      <BasicAlert className={'my-5'} hide={!result.isSuccess} type={'success'}>
        Added promotion code
      </BasicAlert>
      <BasicAlert className={'my-5'} type={'danger'}>{result.error?.message}</BasicAlert>
      <BasicAlert className={'my-5'} hide={!hasDuplicate} type={'warning'}>
        Referral code "{state.referralCode}" has already been used
      </BasicAlert>
      <section className='form-container'>
        <div className={'text-center'}>
          <h2>Referral code</h2>
        </div>
        <ReferralForm disabled={result.isLoading} updateForm={updateForm} form={state} />
        <div className='mt-3 text-center'>
          {backBtn}
          <SubmitButton
            disabled={hasDuplicate || result.isLoading || !state.referralCode}
            color={'secondary'}
            className='ml-5'
            onClick={onSubmit}>Submit</SubmitButton>
        </div>
      </section>
    </div>
  );
};
